import React, { useEffect } from 'react';
import Layout from '../components/Layout';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Search } from 'lucide-react';
import { Input } from "../components/ui/input";

const FAQ = () => {
  useEffect(() => {
    document.title = 'Internjin | FAQ';
  }, []);

  const faqs = [
    {
      category: "General",
      items: [
        { q: "Is Internjin a recruitment agency?", a: "No. Internjin is a matching platform dedicated exclusively to internships, with a pedagogical support component. The goal is training and cross-cultural exchange, not permanent placement." },
        { q: "How long has Internjin existed?", a: "Internjin is a startup in its launch phase, founded by two French students based on their own internship experiences in Japan. Sushi Tech Tokyo is where the first Japanese partners are being sought." },
        { q: "Are you supported by French institutions?", a: "Internjin works in connection with French higher education institutions and is actively seeking institutional partnerships. The model is fully aligned with French legal requirements for internships." }
      ]
    },
    {
      category: "Students",
      items: [
        { q: "What level are the students you propose?", a: "Students come from French business schools, engineering schools, universities, and specialized institutions — from Bachelor level (3-year degree) to Master level (5-year degree), across tech, design, marketing, finance, HR, and more." },
        { q: "Do they speak Japanese?", a: "Some do, but it is not mandatory. Students who can work in English are also selected. Applications can be filtered by language criteria, and all students receive cultural preparation on Japan before departure." },
        { q: "Will the student come to Japan alone?", a: "Yes. The student handles their own travel and accommodation, with assistance from Internjin beforehand. Internjin can also connect companies with local resources to facilitate the student's welcome and integration." },
        { q: "What happens if the internship does not go well?", a: "Internjin acts as a mediator and steps in quickly to find a satisfactory solution for both parties. In extreme cases, an early end to the internship can be arranged per the terms of the agreement." },
        { q: "How do French students handle Japanese workplace culture?", a: "French students tend to be direct and proactive, which can differ from Japanese norms. All students receive intercultural preparation covering communication styles, hierarchy, punctuality, and professional etiquette. Internjin remains available throughout the internship to support both parties." },
        { q: "What if the student has an accident or falls ill in Japan?", a: "The student must have personal health and travel insurance arranged prior to departure. The company is not liable for personal health coverage. Workplace accidents follow responsibilities outlined in the internship agreement, and Internjin can advise both parties." },
        { q: "Can we recruit the student permanently after the internship?", a: "Absolutely — and it happens. If both parties are interested, the company can offer a full-time position at the end of the placement. Internjin fully supports this outcome." }
      ]
    },
    {
      category: "Business Model",
      items: [
        { q: "How much does it cost for the company?", a: "There is no legal obligation to pay the student under Japanese law, but offering a gratification is strongly recommended to attract top candidates. A service fee is also paid to Internjin for matching and follow-up; a detailed fee schedule is provided during the introductory meeting." },
        { q: "Is the student an employee of the company?", a: "No. The student is governed by an internship agreement — no employment contract is required. The internship agreement is the only legal document needed." },
        { q: "What is the minimum and maximum duration of an internship?", a: "Internships range from 2 months to 1 year, depending on student availability, company needs, and the scope of the project." }
      ]
    },
    {
      category: "Practical Steps",
      items: [
        { q: "What steps does the company need to take?", a: "Define your needs and desired profiles, sign the internship agreement, designate an internal mentor, and welcome the student. Internjin handles candidate selection, document management, and ongoing support." },
        { q: "How long does the matching process take?", a: "Selection and matching typically takes 2 to 4 weeks from the moment the offer is posted. Internjin recommends planning at least 6 to 8 weeks ahead of the desired start date to account for visa timelines and academic schedules." },
        { q: "Can we extend or renew the internship if it goes well?", a: "Yes, as long as it remains within the student's academic framework and both parties agree. Any extension must be formalized through an amendment to the original internship agreement, which Internjin will handle." },
        { q: "Does the company need to speak French?", a: "No. Internjin provides bilingual mediation, and all documents can be supplied in English or Japanese." },
        { q: "How do we start a partnership with Internjin?", a: "Simply reach out to schedule a no-commitment discovery call. The team will build a proposal tailored to the company's needs." }
      ]
    }
  ];

  return (
    <Layout>
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Help Center</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8">
            Find answers to common questions about Internjin, the hiring process, and our students.
          </p>
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input 
              placeholder="Search for answers..." 
              className="pl-10 h-12 bg-white/10 border-slate-700 text-white placeholder:text-slate-400 focus-visible:ring-red-500 rounded-full" 
            />
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Tabs defaultValue="General" className="space-y-8">
            <div className="flex justify-center overflow-x-auto pb-4">
              <TabsList className="bg-white p-1 rounded-full border border-slate-200 shadow-sm h-auto inline-flex">
                {faqs.map((cat) => (
                  <TabsTrigger 
                    key={cat.category} 
                    value={cat.category}
                    className="rounded-full px-6 py-3 data-[state=active]:bg-slate-900 data-[state=active]:text-white text-slate-600 font-medium transition-all"
                  >
                    {cat.category}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {faqs.map((cat) => (
              <TabsContent key={cat.category} value={cat.category} className="space-y-6 animate-in fade-in-50 slide-in-from-bottom-4 duration-500">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                    <h2 className="text-2xl font-bold text-slate-900">{cat.category} FAQ</h2>
                  </div>
                  <div className="p-6">
                    <Accordion type="single" collapsible className="w-full space-y-4">
                      {cat.items.map((item, j) => (
                        <AccordionItem key={j} value={`item-${j}`} className="border rounded-lg px-4 data-[state=open]:bg-slate-50 transition-colors border-slate-200">
                          <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-red-600 hover:no-underline py-4">
                            {item.q}
                          </AccordionTrigger>
                          <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                            {item.a}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    </Layout>
  );
};

export default FAQ;
